import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCalendarClockOutline,
  mdiCheckBold,
  mdiCloseCircleOutline,
  mdiEmailCheckOutline,
  mdiEyeCheckOutline,
  mdiProgressClock,
} from "@mdi/js"

import type { CaseCommunicationTimelineItem } from "../../../../../../../../../../api/kehrblatt/case-communications"
import type {
  ContactOrder,
  ContactOrderStep,
} from "../../../../../../../../../../api/kehrblatt/contact-orders"
import { resolvePlannedContactPoint } from "../../../../../_logic/planned-contact-point"
import { buildContactStepExecutionStatusDefinition } from "../../../../../_logic/contact-step-execution-status"
import {
  communicationChannelLabel,
  communicationPurposeLabel,
  formatCommunicationDateTime,
} from "../../../../../_logic/case-communications-presentation"
import { displayContactChannelLabel } from "../../../../../../../../../../_shared/_logic/outreach-presentation"

export type ScheduleEventCommunicationFact = {
  label: string
  value: string
}

export type ScheduleEventCommunicationItem = {
  id: string
  source: "case_communication" | "contact_step"
  title: string
  detail: string
  status: StatusDefinition<string>
  facts: ScheduleEventCommunicationFact[]
  sortAt: string | null
}

type BuildItemsInput = {
  communications?: CaseCommunicationTimelineItem[]
  contactOrder?: ContactOrder | null
  contactPoints?: ContactPoint[]
  customers?: Customer[]
}

const TERMINAL_ORDER_STATUSES = new Set(["CLOSED", "CANCELED", "CANCELLED", "DONE", "COMPLETED"])
const TERMINAL_SCHEDULE_EVENT_STATUSES = new Set(["CANCELED", "CANCELLED", "NOSHOW", "COMPLETED"])
const RAW_CONTACT_TITLE_PREFIX = /^Kontakt\s+per\s+(.+)$/i

const normalizeText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null

  const text = typeof value === "string" ? value : String(value)
  const trimmed = text.trim()

  return trimmed ? trimmed : null
}

const normalizeCode = (value: unknown): string => normalizeText(value)?.toUpperCase() ?? ""

const normalizeContactStepTitle = (value: unknown) => {
  const text = normalizeText(value)

  if (!text) return null

  const prefixedChannel = text.match(RAW_CONTACT_TITLE_PREFIX)?.[1]

  if (prefixedChannel) {
    const channelLabel = displayContactChannelLabel(prefixedChannel)

    return channelLabel === "Kontaktweg" ? text : `Kontakt per ${channelLabel}`
  }

  const channelLabel = displayContactChannelLabel(text)

  return channelLabel === "Kontaktweg" ? text : `Kontakt per ${channelLabel}`
}

const buildById = <T extends { id?: string | null }>(values: T[] = []) =>
  Object.fromEntries(
    values.map((value) => [value.id, value]).filter(([id]) => normalizeText(id)),
  ) as Record<string, T>

const addFact = (facts: ScheduleEventCommunicationFact[], label: string, value: unknown) => {
  const normalized = normalizeText(value)

  if (normalized) facts.push({ label, value: normalized })
}

const relevantCommunicationStepIds = (communications: CaseCommunicationTimelineItem[] = []) => {
  const stepIds = new Set<string>()
  for (const item of communications) {
    const directStepId = normalizeText(item.communication.contact_step_id)

    if (directStepId) stepIds.add(directStepId)

    for (const link of item.links ?? []) {
      if (normalizeCode(link.role) === "CONTACT_STEP") {
        const linkedStepId = normalizeText(link.ref_id)

        if (linkedStepId) stepIds.add(linkedStepId)
      }
    }
  }

  return stepIds
}

const communicationStatusCode = (item: CaseCommunicationTimelineItem) => {
  const communication = item.communication
  const lifecycle = normalizeCode(communication.lifecycle_status)
  const delivery = normalizeCode(communication.delivery_status)
  const outcome = normalizeCode(communication.business_outcome)

  if (lifecycle === "SUPPRESSED") return "SUPPRESSED"

  if (lifecycle === "CANCELED" || outcome === "CANCELED") return "CANCELED"

  if (lifecycle === "FAILED" || delivery === "FAILED") return "FAILED"

  if (delivery === "READ") return "READ"

  if (delivery === "DELIVERED" || delivery === "MANUAL_DELIVERED") return "DELIVERED"

  if (delivery === "ACCEPTED") return "ACCEPTED"

  if (delivery === "SENT") return "SENT"

  if (delivery === "QUEUED" || delivery === "PREPARED" || delivery === "MANUAL_PENDING")
    return delivery

  if (lifecycle === "QUEUED" || lifecycle === "IN_PROGRESS") return lifecycle

  if (lifecycle === "COMPLETED") return "COMPLETED"

  if (lifecycle === "NEEDS_REVIEW") return "NEEDS_REVIEW"

  return lifecycle || "UNKNOWN"
}

export const communicationStatusDefinition = (status: string): StatusDefinition<string> => {
  switch (status) {
    case "SENT":
      return { value: status, label: "Gesendet", icon: mdiCheckBold, color: "emerald" }
    case "ACCEPTED":
      return {
        value: status,
        label: "An Anbieter übergeben",
        icon: mdiEmailCheckOutline,
        color: "blue",
      }
    case "DELIVERED":
      return { value: status, label: "Zugestellt", icon: mdiCheckBold, color: "emerald" }
    case "READ":
      return { value: status, label: "Gelesen", icon: mdiEyeCheckOutline, color: "emerald" }
    case "FAILED":
      return { value: status, label: "Fehlgeschlagen", icon: mdiAlertCircleOutline, color: "rose" }
    case "SUPPRESSED":
      return {
        value: status,
        label: "Ohne Kundenmail",
        icon: mdiCloseCircleOutline,
        color: "neutral",
      }
    case "CANCELED":
      return { value: status, label: "Abgebrochen", icon: mdiCloseCircleOutline, color: "neutral" }
    case "QUEUED":
    case "PREPARED":
    case "IN_PROGRESS":
      return { value: status, label: "Wird gesendet", icon: mdiProgressClock, color: "blue" }
    case "MANUAL_PENDING":
      return {
        value: status,
        label: "Manuell offen",
        icon: mdiCalendarClockOutline,
        color: "orange",
      }
    case "COMPLETED":
      return { value: status, label: "Erledigt", icon: mdiCheckBold, color: "emerald" }
    case "NEEDS_REVIEW":
      return { value: status, label: "Prüfen", icon: mdiAlertCircleOutline, color: "amber" }
    case "PLANNED":
      return { value: status, label: "Geplant", icon: mdiCalendarClockOutline, color: "blue" }
    case "CLOSED":
      return { value: status, label: "Abgeschlossen", icon: mdiCheckBold, color: "emerald" }
    default:
      return {
        value: status || "UNKNOWN",
        label: "Status unbekannt",
        icon: mdiProgressClock,
        color: "blue",
      }
  }
}

const communicationTimestamp = (item: CaseCommunicationTimelineItem) => {
  const communication = item.communication

  return (
    communication.read_at ||
    communication.delivered_at ||
    communication.accepted_at ||
    communication.sent_at ||
    communication.queued_at ||
    communication.completed_at ||
    communication.failed_at ||
    communication.canceled_at ||
    communication.suppressed_at ||
    communication.planned_at ||
    communication.due_at ||
    communication.created_at ||
    null
  )
}

const communicationDetail = (item: CaseCommunicationTimelineItem, status: string) => {
  const communication = item.communication
  const channel = communicationChannelLabel(communication.channel)
  const transport = channel ? ` per ${channel}` : ""
  switch (status) {
    case "SENT":
      return `Nachricht wurde${transport} gesendet.`
    case "ACCEPTED":
      return "Nachricht wurde an den Anbieter übergeben."
    case "DELIVERED":
      return "Nachricht wurde zugestellt."
    case "READ":
      return "Nachricht wurde gelesen."
    case "FAILED":
      return communication.provider_status
        ? `Nachricht konnte nicht gesendet werden: ${communication.provider_status}.`
        : "Nachricht konnte nicht gesendet werden."
    case "SUPPRESSED":
      return "Termin wurde ohne separate Kundenmail dokumentiert."
    case "CANCELED":
      return "Kommunikation wurde abgebrochen."
    case "QUEUED":
    case "PREPARED":
    case "IN_PROGRESS":
      return "Nachricht ist für den Versand vorbereitet."
    default:
      return "Kommunikation zum Termin wurde dokumentiert."
  }
}

export const buildCaseCommunicationItem = (
  item: CaseCommunicationTimelineItem,
): ScheduleEventCommunicationItem => {
  const communication = item.communication
  const recipient = item.kehrblatt_extension?.recipient
  const status = communicationStatusCode(item)
  const timestamp = communicationTimestamp(item)
  const timestampLabel = formatCommunicationDateTime(timestamp)
  const titleBase = communicationPurposeLabel(communication.purpose)
  const facts: ScheduleEventCommunicationFact[] = []
  const recipientValue =
    normalizeText(recipient?.target_value) || normalizeText(recipient?.display_name)
  const subject = normalizeText(item.kehrblatt_extension?.content?.subject)

  addFact(facts, "Kanal", communicationChannelLabel(communication.channel))
  addFact(facts, "Empfänger", recipientValue)
  addFact(facts, "Betreff", subject)

  return {
    id: `communication:${communication.id}`,
    source: "case_communication",
    title: timestampLabel ? `${titleBase} vom ${timestampLabel}` : titleBase,
    detail: communicationDetail(item, status),
    status: communicationStatusDefinition(status),
    facts,
    sortAt: timestamp,
  }
}

const stepRecipient = (
  step: ContactOrderStep,
  contactPointById: Record<string, ContactPoint>,
  customerById: Record<string, Customer>,
) => {
  const customer = step.recipient_customer_id ? customerById[step.recipient_customer_id] : null
  const contactPoint = resolvePlannedContactPoint(step, contactPointById)

  return (
    normalizeText(customer?.display_name) ||
    normalizeText(step.destination?.label) ||
    normalizeText(step.destination?.value) ||
    normalizeText(contactPoint?.label) ||
    normalizeText(contactPoint?.normalized_value) ||
    normalizeText(contactPoint?.value)
  )
}

const stepTitle = (step: ContactOrderStep) => {
  const channel = displayContactChannelLabel(step.channel)

  return (
    normalizeContactStepTitle(step.title) ||
    normalizeContactStepTitle(step.message_label) ||
    normalizeContactStepTitle(step.template_label) ||
    (channel ? `Kontakt per ${channel}` : "Kontaktschritt")
  )
}

const stepSortAt = (step: ContactOrderStep) =>
  step.due_at || step.next_action_at || step.response_deadline_at || null

export const buildContactStepItem = (
  step: ContactOrderStep,
  contactPointById: Record<string, ContactPoint> = {},
  customerById: Record<string, Customer> = {},
): ScheduleEventCommunicationItem => {
  const facts: ScheduleEventCommunicationFact[] = []
  const channel = displayContactChannelLabel(step.channel)
  const dueAt = step.due_at ? formatCommunicationDateTime(step.due_at) : null
  const status = buildContactStepExecutionStatusDefinition(step.status)

  addFact(facts, "Kontaktweg", channel)
  addFact(facts, "Empfänger", stepRecipient(step, contactPointById, customerById))
  addFact(facts, "Fällig", dueAt)

  return {
    id: `step:${step.id}`,
    source: "contact_step",
    title: stepTitle(step),
    detail:
      normalizeText(step.result_description) || status.description || "Geplanter Kontaktschritt.",
    status,
    facts,
    sortAt: stepSortAt(step),
  }
}

const sortedContactSteps = (contactOrder?: ContactOrder | null) =>
  [...(contactOrder?.steps ?? [])].sort((left, right) => {
    const positionDiff = (left.position ?? 0) - (right.position ?? 0)

    if (positionDiff !== 0) return positionDiff

    return (stepSortAt(left) ?? "").localeCompare(stepSortAt(right) ?? "")
  })

const sortCommunicationsNewestFirst = (items: ScheduleEventCommunicationItem[]) =>
  [...items].sort((left, right) => {
    const leftMs = Date.parse(left.sortAt || "")
    const rightMs = Date.parse(right.sortAt || "")

    return (Number.isFinite(rightMs) ? rightMs : 0) - (Number.isFinite(leftMs) ? leftMs : 0)
  })

export const buildScheduleEventCommunicationItems = ({
  communications = [],
  contactOrder = null,
  contactPoints = [],
  customers = [],
}: BuildItemsInput): ScheduleEventCommunicationItem[] => {
  const contactPointById = buildById(contactPoints)
  const customerById = buildById(customers)
  const communicationStepIds = relevantCommunicationStepIds(communications)
  const communicationItems = sortCommunicationsNewestFirst(
    communications.map(buildCaseCommunicationItem),
  )
  const stepItems = sortedContactSteps(contactOrder)
    .filter((step) => !communicationStepIds.has(step.id))
    .map((step) => buildContactStepItem(step, contactPointById, customerById))

  return [...communicationItems, ...stepItems]
}

export const findScheduleEventContactOrder = (
  contactOrders: ContactOrder[] = [],
  event?: Pick<ScheduleEvent, "id" | "contact_order_id"> | null,
) => {
  const orders = contactOrders.filter(
    (order) => order.schedule_event_id === event?.id || order.id === event?.contact_order_id,
  )

  return (
    orders.find((order) => !TERMINAL_ORDER_STATUSES.has(normalizeCode(order.status))) ??
    orders[0] ??
    null
  )
}

export const isTerminalScheduleEventContactOrder = (contactOrder?: ContactOrder | null) =>
  TERMINAL_ORDER_STATUSES.has(normalizeCode(contactOrder?.status))

const isTerminalScheduleEvent = (event?: Pick<ScheduleEvent, "status"> | null) =>
  TERMINAL_SCHEDULE_EVENT_STATUSES.has(normalizeCode(event?.status))

export const findActiveScheduleEventContactOrder = (
  contactOrders: ContactOrder[] = [],
  event?: Pick<ScheduleEvent, "id" | "contact_order_id" | "status"> | null,
) => {
  if (isTerminalScheduleEvent(event)) return null

  const orders = contactOrders.filter(
    (order) => order.schedule_event_id === event?.id || order.id === event?.contact_order_id,
  )

  return orders.find((order) => !isTerminalScheduleEventContactOrder(order)) ?? null
}
